import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import api from '../api';

function UserDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUserData();
  }, [id]);

  const loadUserData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/users/${id}`);
      setUserData(res.data);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError(err.response?.status === 404 ? 'Benutzer nicht gefunden' : 'Fehler beim Laden der Benutzerdaten');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} Min.`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const goBack = () => {
    history.push('/admin', { tab: 'users' });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <button onClick={goBack} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
          &larr; Zurück
        </button>
        <div style={{ textAlign: 'center', padding: '2rem' }}>Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <button onClick={goBack} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
          &larr; Zurück
        </button>
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#C62828' }}>
          {error}
        </div>
      </div>
    );
  }

  const { user, statistics, past_shifts, upcoming_shifts } = userData;

  return (
    <div>
      <button onClick={goBack} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
        &larr; Zurück
      </button>

      {/* User Info Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {user.first_name || user.last_name ? (
                `${user.first_name || ''} ${user.last_name || ''}`.trim()
              ) : (
                <span style={{ color: '#999', fontStyle: 'italic' }}>Kein Name</span>
              )}
              {user.is_super_admin && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#ff9800',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  SUPER ADMIN
                </span>
              )}
              {user.is_admin && !user.is_super_admin && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  ADMIN
                </span>
              )}
            </h2>
            <div style={{ color: '#666', fontSize: '0.95rem' }}>
              <div style={{ marginBottom: '0.25rem' }}>
                <strong>E-Mail:</strong> <a href={`mailto:${user.email}`} style={{ color: '#C62828' }}>{user.email}</a>
              </div>
              {user.mobile && (
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Mobile:</strong> <a href={`tel:${user.mobile}`} style={{ color: '#C62828' }}>{user.mobile}</a>
                </div>
              )}
              {user.contact_id && (
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Fairgate ID:</strong> {user.contact_id}
                </div>
              )}
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
                Registriert am {formatDate(user.created_at)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div className="card" style={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1565c0' }}>
            {statistics.total_shifts}
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Total Einsätze</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#e8f5e9' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32' }}>
            {statistics.past_shifts_count}
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Geleistet</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#fff3e0' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef6c00' }}>
            {statistics.upcoming_shifts_count}
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Anstehend</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#fce4ec' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c2185b' }}>
            {statistics.past_hours}h
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Geleistete Stunden</div>
        </div>

        <div className="card" style={{ textAlign: 'center', backgroundColor: '#f3e5f5' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7b1fa2' }}>
            {statistics.total_hours}h
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Total Stunden</div>
        </div>
      </div>

      {/* Upcoming Shifts */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{
          marginBottom: '1rem',
          color: '#ef6c00',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>&#9203;</span>
          Anstehende Einsätze ({statistics.upcoming_shifts_count})
          {statistics.upcoming_hours > 0 && (
            <span style={{
              fontWeight: 'normal',
              fontSize: '0.9rem',
              color: '#666',
              marginLeft: 'auto'
            }}>
              {statistics.upcoming_hours}h geplant
            </span>
          )}
        </h3>

        {upcoming_shifts.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
            Keine anstehenden Einsätze.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {upcoming_shifts.map((shift) => (
              <Link
                key={`${shift.event_id}-${shift.shift_id}`}
                to={`/event/${shift.event_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3e0',
                  borderRadius: '8px',
                  border: '1px solid #ffcc80',
                  transition: 'transform 0.1s, box-shadow 0.1s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong style={{ color: '#e65100' }}>{shift.event_title}</strong>
                      <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                        {formatDate(shift.event_date)}
                      </div>
                      <div style={{ color: '#888', fontSize: '0.85rem' }}>
                        {shift.event_location}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#ef6c00',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        {shift.shift_name}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        {shift.start_time} Uhr - {formatDuration(shift.duration_minutes)}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Past Shifts */}
      <div className="card">
        <h3 style={{
          marginBottom: '1rem',
          color: '#2e7d32',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>&#10003;</span>
          Geleistete Einsätze ({statistics.past_shifts_count})
          {statistics.past_hours > 0 && (
            <span style={{
              fontWeight: 'normal',
              fontSize: '0.9rem',
              color: '#666',
              marginLeft: 'auto'
            }}>
              Total: {statistics.past_hours}h
            </span>
          )}
        </h3>

        {past_shifts.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
            Noch keine geleisteten Einsätze.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {past_shifts.map((shift) => (
              <Link
                key={`${shift.event_id}-${shift.shift_id}`}
                to={`/event/${shift.event_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  borderLeft: '4px solid #4caf50',
                  transition: 'background-color 0.1s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#e8f5e9';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong>{shift.event_title}</strong>
                      <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                        - {shift.shift_name}
                      </span>
                      <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                        {formatDate(shift.event_date)} - {shift.event_location}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#e8f5e9',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      color: '#2e7d32',
                      fontWeight: 'bold'
                    }}>
                      {formatDuration(shift.duration_minutes)}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDetail;
